import { values } from 'ramda';
import type { BillingDataPlatform } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import type { RootState } from 'reactApp/store';
import type { LoadingState } from 'reactApp/types/commonStates';
import { createSelector } from 'reselect';

import type { PastSubscriptionsState } from './pastSubscriptions.types';

const getPastSubscriptionsRoot = (state: RootState) => state.pastSubscriptions;
const getPastSubscriptions = createSelector(getPastSubscriptionsRoot, ({ subscription }) => subscription);
const getPastSubscriptionsList = createSelector(getPastSubscriptions, (list) => values(list));
const getPastSubscriptionsListByPlatform = createSelector(
    getPastSubscriptionsList,
    (state, type: BillingDataPlatform) => type,
    (list, type: BillingDataPlatform) => {
        return list.filter((sub) => sub.type === type);
    }
);

const wasSubsGreaterOrEqualThan = createSelector(
    (state, _) => getPastSubscriptionsList(state),
    (_, quota: number) => quota,
    (subscriptions, quota) => subscriptions.some((subs) => subs.space.original >= quota && subs.flags.paidFeatures)
);

const sortByExpires = (subs: BillingDataSubscriptionInfo[]) => subs.sort((a, b) => b.expires - a.expires)[0];
const latestSubsByExpires = createSelector(getPastSubscriptionsList, sortByExpires);
const latestSubsByExpiresByPlatform = createSelector(getPastSubscriptionsListByPlatform, sortByExpires);

const getLoadingState = createSelector(
    getPastSubscriptionsRoot,
    (state: PastSubscriptionsState): LoadingState => ({
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
    })
);

const getPastSubscriptionsByProductIds = createSelector(
    getPastSubscriptionsList,
    (_: RootState, productIds: string[]) => productIds,
    (subscriptions, productIds) => {
        return subscriptions.filter((sub) => productIds.includes(sub.productId));
    }
);

export const PastSubscriptionsSelectors = {
    latestSubsByExpiresByPlatform,
    wasSubsGreaterOrEqualThan,
    latestSubsByExpires,
    getLoadingState,
    getPastSubscriptionsByProductIds,
} as const;
